import { LineItem, DiscountCodeInfo } from "@commercetools/platform-sdk"
import { DEFAULT_REGION, IS } from "../i18n/Region"

interface discountNamesForItemInput {
  item: LineItem
  language: string
  cartDiscountCodes?: DiscountCodeInfo[]
}

export const FALLBACK_CART_DISCOUNT_NAME = {
  en: "Bag promotion",
  is: "afsláttarkóði"
}

export const getAllDiscountNamesForItem = ({
  item,
  language,
  cartDiscountCodes = []
}: discountNamesForItemInput): Array<string> => {
  const itemDiscountName: string | undefined = getLineItemDiscountName({
    item,
    language
  })
  const cartDiscountCodeNames: Array<string> =
    getApplicableCartDiscountNamesForItem({ cartDiscountCodes, item, language })
  return itemDiscountName
    ? [itemDiscountName].concat(cartDiscountCodeNames)
    : cartDiscountCodeNames
}

const isDiscountApplicableToLineItem = ({ item, cartDiscountId }) =>
  item.discountedPrice?.includedDiscounts.find(
    ({ discount }) =>
      discount.typeId === "cart-discount" && discount.id === cartDiscountId
  )

const getLineItemDiscountName = ({ item, language }) => {
  const discountObj = item.price?.discounted?.discount.obj
  if (discountObj) {
    return (
      discountObj.name[language] ||
      discountObj.name[DEFAULT_REGION.language] ||
      discountObj.name[IS.language]
    )
  }
  return null
}

const getApplicableCartDiscountCodesForItem = ({ item, cartDiscountCodes }) =>
  cartDiscountCodes.filter(cdi =>
    cdi.discountCode.obj?.cartDiscounts.find(cartDiscount =>
      isDiscountApplicableToLineItem({
        item,
        cartDiscountId: cartDiscount.id
      })
    )
  )

const getApplicableCartDiscountNamesForItem = ({
  cartDiscountCodes,
  item,
  language
}) =>
  filterCartDiscountNames({
    cartDiscountCodes: getApplicableCartDiscountCodesForItem({
      item,
      cartDiscountCodes
    }),
    language
  })

const filterCartDiscountNames = ({ cartDiscountCodes, language }) =>
  cartDiscountCodes
    .map(({ discountCode }: DiscountCodeInfo) =>
      discountCode.obj?.name
        ? discountCode.obj.name[language] ||
          discountCode.obj.name[DEFAULT_REGION.language] ||
          FALLBACK_CART_DISCOUNT_NAME[language]
        : FALLBACK_CART_DISCOUNT_NAME[language]
    )
    .filter(name => name)

export const getInvalidDiscountCodesForItems = ({
  cartDiscountCodes = [],
  lineItems = [],
  language
}: {
  cartDiscountCodes?: Array<DiscountCodeInfo>
  lineItems?: Array<LineItem>
  language: string
}) => {
  const invalidDiscountCodes: Array<string> = []
  const invalidDiscountItems: Array<string> = []

  if (!cartDiscountCodes.length || !lineItems.length)
    return { invalidDiscountItems, invalidDiscountCodes }

  cartDiscountCodes.forEach(({ discountCode }: DiscountCodeInfo) => {
    const { cartDiscounts = [], code = "" } = discountCode.obj || {}

    lineItems.forEach((item: LineItem) => {
      const itemName = (item.name && item.name[language]) || ""
      const itemAndSkuName = `${itemName} (${item.variant.sku})`
      const isDiscountApplicableToItem = cartDiscounts.find(cartDiscount =>
        isDiscountApplicableToLineItem({
          item,
          cartDiscountId: cartDiscount.id
        })
      )

      if (!isDiscountApplicableToItem) {
        if (invalidDiscountCodes.indexOf(code) === -1)
          invalidDiscountCodes.push(code)

        if (invalidDiscountItems.indexOf(itemAndSkuName) === -1)
          invalidDiscountItems.push(itemAndSkuName)
      }
    })
  })

  return {
    invalidDiscountCodes,
    invalidDiscountItems
  }
}
